import { Component, computed, effect, input, signal } from '@angular/core';

import {
  ErrorAlertComponent,
  TApiErrorResponse,
} from '@sandbox-platform/shared-ui';

import {
  PendingCommissionTask,
  PendingReferralTask,
  PendingTaskKind,
} from '../../pending-referral-tasks.definitions';
import { PendingCommissionTaskCardComponent } from './pending-commission-task-card.component';
import { PendingReferralTaskCardComponent } from './pending-referral-task-card.component';

type PendingReferralTaskTab = {
  kind: PendingTaskKind;
  label: string;
  emptyTitle: string;
};

@Component({
  standalone: true,
  imports: [
    ErrorAlertComponent,
    PendingCommissionTaskCardComponent,
    PendingReferralTaskCardComponent,
  ],
  selector: 'lib-pending-referral-tasks-section',
  templateUrl: './pending-referral-tasks-section.component.html',
})
export class PendingReferralTasksSectionComponent {
  needsAcceptanceTasks = input.required<PendingReferralTask[]>();
  waitingOnRefereeTasks = input.required<PendingReferralTask[]>();
  commissionDueTasks = input<PendingCommissionTask[]>([]);
  loading = input(false);
  error = input<string | TApiErrorResponse | null>(null);

  protected readonly loadingRows = [1, 2, 3];
  protected readonly tabs: PendingReferralTaskTab[] = [
    {
      kind: 'needs_referee_acceptance',
      label: 'Needs acceptance',
      emptyTitle: 'No referrals need your organization acceptance.',
    },
    {
      kind: 'waiting_on_referee',
      label: 'Waiting on referee',
      emptyTitle: 'No referrals are waiting on referee acceptance.',
    },
    {
      kind: 'commission_due',
      label: 'Commissions due',
      emptyTitle: 'No commissions are due for invoice creation.',
    },
  ];

  protected readonly selectedKind = signal<PendingTaskKind>(
    'needs_referee_acceptance'
  );

  constructor() {
    effect(() => {
      if (
        !this.loading() &&
        this.selectedKind() === 'needs_referee_acceptance' &&
        this.needsAcceptanceTasks().length === 0 &&
        (this.waitingOnRefereeTasks().length > 0 ||
          this.commissionDueTasks().length > 0)
      ) {
        this.selectedKind.set(
          this.waitingOnRefereeTasks().length > 0
            ? 'waiting_on_referee'
            : 'commission_due'
        );
      }
    });
  }

  protected readonly selectedTab = computed(
    () =>
      this.tabs.find((tab) => tab.kind === this.selectedKind()) ?? this.tabs[0]
  );

  protected readonly selectedTasks = computed(() =>
    this.selectedKind() === 'needs_referee_acceptance'
      ? this.needsAcceptanceTasks()
      : this.selectedKind() === 'waiting_on_referee'
      ? this.waitingOnRefereeTasks()
      : this.commissionDueTasks()
  );

  protected countFor(kind: PendingTaskKind): number {
    return kind === 'needs_referee_acceptance'
      ? this.needsAcceptanceTasks().length
      : kind === 'waiting_on_referee'
      ? this.waitingOnRefereeTasks().length
      : this.commissionDueTasks().length;
  }

  protected isCommissionTask(
    task: PendingReferralTask | PendingCommissionTask
  ): task is PendingCommissionTask {
    return task.kind === 'commission_due';
  }

  protected taskKey(task: PendingReferralTask | PendingCommissionTask): string {
    return this.isCommissionTask(task)
      ? task.commission.commission_id
      : task.referral.id;
  }
}
